// store/selectors/orderSelectors.js
// ============================================================================
// ORDER SELECTORS - Including Karigar Status Selectors
// ============================================================================
// Note: Karigar operation selectors (available karigars, queue, etc.) are in
// karigarSelectors.js. These selectors only read karigar data FROM the order.
// ============================================================================

import { createSelector } from "@reduxjs/toolkit";

// ============================================================================
// BASE SELECTOR
// ============================================================================

export const selectOrderState = (state) => state.orders || {};

// ============================================================================
// ORDER SELECTORS
// ============================================================================

export const selectOrders = (state) => state.orders.orders || [];
export const selectCurrentOrder = (state) => state.orders.order || null;
export const selectDraftOrder = (state) => state.orders.draftOrder || null;
export const selectOrderStats = (state) => state.orders.orderStats || null;
export const selectTrackingInfo = (state) => state.orders.trackingInfo || null;

// Loading states
export const selectOrderLoading = (state) => state.orders.loading || false;
export const selectOrderListLoading = (state) =>
  state.orders.loadingList || false;
export const selectOrderDraftLoading = (state) =>
  state.orders.loadingDraft || false;
export const selectOrderApprovalLoading = (state) =>
  state.orders.loadingApproval || false;
export const selectOrderError = (state) => state.orders.error || null;

// Pagination
export const selectOrderPagination = (state) =>
  state.orders.pagination || {
    total: 0,
    page: 1,
    limit: 20,
    pages: 0,
  };

// Filters
export const selectOrderFilters = (state) => state.orders.filters || {};

// Draft editing
export const selectDraftEditing = (state) => state.orders.draftEditing || {};
export const selectCanEditDraft = (state) =>
  state.orders.draftEditing?.canEdit || false;
export const selectDraftRequiresApproval = (state) =>
  state.orders.draftEditing?.requiresApproval || false;
export const selectIsDraftLocked = (state) =>
  state.orders.draftEditing?.isLocked || false;

// Selected orders (for bulk operations)
export const selectSelectedOrders = (state) =>
  state.orders.selectedOrders || [];

// ============================================================================
// PRICING SELECTORS
// ============================================================================

export const selectPricing = (state) => state.orders.pricing || {};
export const selectCalculatedPricing = (state) =>
  state.orders.pricing?.calculated || null;
export const selectPricingLoading = (state) =>
  state.orders.pricing?.loading || false;
export const selectPricingError = (state) =>
  state.orders.pricing?.error || null;
export const selectLastPricingCalculation = (state) =>
  state.orders.pricing?.lastCalculatedAt || null;

// ============================================================================
// ADJUSTMENTS SELECTORS
// ============================================================================

export const selectAdjustments = (state) => state.orders.adjustments || {};
export const selectAdjustmentsList = (state) =>
  state.orders.adjustments?.list || [];
export const selectPendingAdjustments = (state) =>
  state.orders.adjustments?.pending || [];
export const selectActiveAdjustments = (state) =>
  state.orders.adjustments?.active || [];
export const selectAdjustmentsLoading = (state) =>
  state.orders.adjustments?.loading || false;
export const selectAdjustmentsError = (state) =>
  state.orders.adjustments?.error || null;

// ============================================================================
// EXCHANGE ITEMS SELECTORS
// ============================================================================

export const selectExchangeItems = (state) => state.orders.exchangeItems || {};
export const selectExchangeItemsList = (state) =>
  state.orders.exchangeItems?.list || [];
export const selectAcceptedExchangeItems = (state) =>
  state.orders.exchangeItems?.accepted || [];
export const selectExchangeItemsLoading = (state) =>
  state.orders.exchangeItems?.loading || false;
export const selectExchangeItemsError = (state) =>
  state.orders.exchangeItems?.error || null;
export const selectExchangeCalculation = (state) =>
  state.orders.exchangeItems?.calculation || {};
export const selectExchangeAppraisal = (state) =>
  state.orders.exchangeItems?.calculation?.appraisal || null;

// ============================================================================
// FULFILLMENT SELECTORS
// ============================================================================

export const selectFulfillmentDetails = (state) =>
  state.orders.fulfillmentDetails || null;
export const selectFulfillmentLoading = (state) =>
  state.orders.loadingFulfillment || false;
export const selectFulfillmentError = (state) =>
  state.orders.fulfillmentError || null;
export const selectFulfillmentUI = (state) => state.orders.fulfillmentUI || {};
export const selectFulfillmentType = (state) =>
  state.orders.fulfillmentUI?.type || null;

// Order fulfillment details
export const selectOrderFulfillment = (state) =>
  state.orders.order?.fulfillment || null;
export const selectPickupDetails = (state) =>
  state.orders.order?.fulfillment?.pickup || null;
export const selectDeliveryDetails = (state) =>
  state.orders.order?.fulfillment?.delivery || null;
export const selectFulfillmentDateHistory = (state) =>
  state.orders.order?.fulfillment?.dateChangeHistory || [];

// Check if order can be rescheduled
export const selectCanReschedule = createSelector(
  [selectCurrentOrder],
  (order) => {
    if (!order) return false;
    const allowedStatuses = ["PENDING", "CONFIRMED", "PROCESSING", "READY"];
    return allowedStatuses.includes(order.status);
  }
);

// Check if pickup is scheduled
export const selectIsPickupScheduled = createSelector(
  [selectOrderFulfillment],
  (fulfillment) =>
    fulfillment?.type === "PICKUP" && fulfillment?.pickup?.scheduledDate != null
);

// Check if delivery is scheduled
export const selectIsDeliveryScheduled = createSelector(
  [selectOrderFulfillment],
  (fulfillment) =>
    fulfillment?.type === "DELIVERY" &&
    fulfillment?.delivery?.scheduledDate != null
);

// Get next scheduled date
export const selectNextScheduledDate = createSelector(
  [selectOrderFulfillment],
  (fulfillment) => {
    if (!fulfillment) return null;
    if (fulfillment.type === "PICKUP") return fulfillment.pickup?.scheduledDate;
    if (fulfillment.type === "DELIVERY")
      return fulfillment.delivery?.scheduledDate;
    return null;
  }
);

// Delivery attempts
export const selectDeliveryAttempts = (state) =>
  state.orders.order?.fulfillment?.delivery?.attempts || [];
export const selectDeliveryAttemptCount = (state) =>
  state.orders.order?.fulfillment?.delivery?.attemptCount || 0;

// ============================================================================
// REFUND SELECTORS
// ============================================================================

export const selectRefund = (state) => state.orders.refund || {};
export const selectCurrentRefund = (state) =>
  state.orders.refund?.currentRefund || null;
export const selectRefundLoading = (state) =>
  state.orders.refund?.loading || false;
export const selectRefundError = (state) => state.orders.refund?.error || null;

export const selectPendingRefunds = (state) =>
  state.orders.pendingRefunds || {};
export const selectPendingRefundsList = (state) =>
  state.orders.pendingRefunds?.list || [];
export const selectPendingRefundsLoading = (state) =>
  state.orders.pendingRefunds?.loading || false;
export const selectPendingRefundsPagination = (state) =>
  state.orders.pendingRefunds?.pagination || {};

export const selectRefundStats = (state) => state.orders.refundStats || {};

// ============================================================================
// KARIGAR STATUS SELECTORS (from order object)
// ============================================================================
// Note: These read karigar data that's stored in the order.
// For karigar operations (assignment, queue, etc.), use karigarSelectors.js

/**
 * Get karigar assignments array from order
 * Each assignment contains: orderItemIndex, karigarId, karigarCode, karigarName,
 * jobId, jobNumber, status, assignedAt, etc.
 */
export const selectOrderKarigarAssignments = createSelector(
  [selectCurrentOrder],
  (order) => order?.karigarAssignments || []
);

/**
 * Get overall karigar work status for the order
 * Values: NOT_ASSIGNED, PARTIAL, IN_PROGRESS, COMPLETED
 */
export const selectOrderKarigarWorkStatus = createSelector(
  [selectCurrentOrder],
  (order) => order?.karigarWorkStatus || "NOT_ASSIGNED"
);

/**
 * Get karigar assignment for a specific order item
 */
export const selectKarigarAssignmentForItem = (itemIndex) =>
  createSelector(
    [selectOrderKarigarAssignments],
    (assignments) =>
      assignments.find((a) => a.orderItemIndex === itemIndex) || null
  );

/**
 * Get order items that don't have karigar assigned
 */
export const selectOrderItemsWithoutKarigar = createSelector(
  [selectCurrentOrder],
  (order) => {
    if (!order?.items) return [];

    const assignedIndices = new Set(
      (order.karigarAssignments || []).map((a) => a.orderItemIndex)
    );

    return order.items
      .map((item, index) => ({ ...item, index }))
      .filter((item) => !assignedIndices.has(item.index));
  }
);

/**
 * Get order items that have karigar assigned (with assignment data)
 */
export const selectOrderItemsWithKarigar = createSelector(
  [selectCurrentOrder],
  (order) => {
    if (!order?.items) return [];

    const assignmentMap = {};
    (order.karigarAssignments || []).forEach((a) => {
      assignmentMap[a.orderItemIndex] = a;
    });

    return order.items
      .map((item, index) => ({
        ...item,
        index,
        karigarAssignment: assignmentMap[index] || null,
      }))
      .filter((item) => item.karigarAssignment);
  }
);

/**
 * Check if karigar can be assigned to this order (based on order status)
 */
export const selectCanAssignKarigarToOrder = createSelector(
  [selectCurrentOrder],
  (order) => {
    if (!order) return false;
    const allowedStatuses = ["DRAFT", "PENDING", "CONFIRMED", "PROCESSING"];
    return allowedStatuses.includes(order.status);
  }
);

/**
 * Check if all items have karigar assigned
 */
export const selectAllItemsHaveKarigar = createSelector(
  [selectCurrentOrder],
  (order) => {
    if (!order?.items?.length) return false;
    const assignedCount = order.karigarAssignments?.length || 0;
    return assignedCount >= order.items.length;
  }
);

/**
 * Check if any items have karigar assigned
 */
export const selectHasAnyKarigarAssigned = createSelector(
  [selectOrderKarigarAssignments],
  (assignments) => assignments.length > 0
);

/**
 * Get count of items needing karigar assignment
 */
export const selectItemsNeedingKarigarCount = createSelector(
  [selectOrderItemsWithoutKarigar],
  (items) => items.length
);

/**
 * Check if all karigar work is complete
 */
export const selectAllKarigarWorkComplete = createSelector(
  [selectOrderKarigarWorkStatus],
  (status) => status === "COMPLETED"
);

/**
 * Check if order can be fulfilled based on karigar work
 */
export const selectCanFulfillBasedOnKarigar = createSelector(
  [selectCurrentOrder, selectAllKarigarWorkComplete],
  (order, allComplete) => {
    // If no karigar work required, can fulfill
    if (!order?.karigarAssignments?.length) return true;
    // Otherwise, all work must be complete
    return allComplete;
  }
);

// ============================================================================
// KARIGAR UI SELECTORS
// ============================================================================

export const selectKarigarUI = (state) => state.orders.karigarUI || {};

export const selectShowKarigarAssignmentModal = createSelector(
  [selectKarigarUI],
  (ui) => ui.showAssignmentModal || false
);

export const selectSelectedItemForKarigar = createSelector(
  [selectKarigarUI],
  (ui) => ui.selectedItemIndex
);

export const selectSelectedProductForKarigar = createSelector(
  [selectKarigarUI],
  (ui) => ui.selectedProductId
);

export const selectShowKarigarStatusPanel = createSelector(
  [selectKarigarUI],
  (ui) => ui.showStatusPanel || false
);

export const selectShowKarigarQueueModal = createSelector(
  [selectKarigarUI],
  (ui) => ui.showQueueModal || false
);

export const selectSelectedKarigarForQueue = createSelector(
  [selectKarigarUI],
  (ui) => ui.selectedKarigarId
);

// ============================================================================
// COMPOSITE SELECTORS
// ============================================================================

/**
 * Get order with karigar summary
 */
export const selectOrderWithKarigarSummary = createSelector(
  [
    selectCurrentOrder,
    selectOrderKarigarAssignments,
    selectOrderKarigarWorkStatus,
    selectAllKarigarWorkComplete,
    selectItemsNeedingKarigarCount,
  ],
  (order, assignments, workStatus, allComplete, needingCount) => {
    if (!order) return null;

    return {
      ...order,
      karigarSummary: {
        totalItems: order.items?.length || 0,
        assignedItems: assignments.length,
        pendingItems: needingCount,
        workStatus,
        allComplete,
        canFulfill: allComplete || assignments.length === 0,
      },
    };
  }
);

/**
 * Get items with full karigar status for display
 */
export const selectItemsWithKarigarStatus = createSelector(
  [selectCurrentOrder],
  (order) => {
    if (!order?.items) return [];

    const assignmentMap = {};
    (order.karigarAssignments || []).forEach((a) => {
      assignmentMap[a.orderItemIndex] = a;
    });

    return order.items.map((item, index) => {
      const assignment = assignmentMap[index];
      return {
        ...item,
        index,
        karigar: assignment
          ? {
              isAssigned: true,
              karigarId: assignment.karigarId,
              karigarCode: assignment.karigarCode,
              karigarName: assignment.karigarName,
              jobId: assignment.jobId,
              jobNumber: assignment.jobNumber,
              status: assignment.status,
              assignedAt: assignment.assignedAt,
              expectedCompletion: assignment.timeline?.expectedCompletionDate,
            }
          : {
              isAssigned: false,
              karigarId: null,
              status: "PENDING",
            },
      };
    });
  }
);
